import { useEffect, useCallback, useMemo } from 'react';
import {
  Badge, Text,
  Container, Flex, Stack,
  LoadingOverlay, Alert,
  Button, rem,
  SegmentedControl
} from '@mantine/core';
import { useForm } from 'react-hook-form';
import { ShipmentProvider, useShipmentContext } from '../contexts/ShipmentContext';
import { IconAlertCircle, IconTruck, IconPackageImport, IconTruckDelivery } from '@tabler/icons-react';
import PageTitle from '../components/common/PageTitle';
import EmptyState from '../components/shipment/EmptyState';
import ShipmentList from '../components/shipment/ShipmentList';
import { ShipmentService } from '../types';
import { Shipment } from '../components/shipment/types';

// Define the shipment type

type FilterFormValues = {
  statusFilter: 'all' | 'processing' | 'shipped' | 'delivered';
};

const Shipments = () => {
  const { reset } = useForm<FilterFormValues>({
    defaultValues: {
      statusFilter: 'all'
    }
  });
  const iconStyle = { width: rem(18), height: rem(18) };

  // Use ShipmentContext for state management
  const { state, dispatch } = useShipmentContext();
  const { shipments, loading, error, filters } = state;
  const isFiltering = filters.statusFilter !== 'all';

  // Map API state to our status - memoized to avoid recreation
  const mapStateToStatus = useCallback((state: string): 'processing' | 'shipped' | 'delivered' => {
    switch (state) {
      case 'REQUESTED':
        return 'processing';
      case 'APPROVED':
        return 'shipped';
      case 'DELIVERED':
        return 'delivered';
      default:
        return 'processing';
    }
  }, []);

  // Transform raw API data to our Shipment format - improved with proper error handling
  const transformShipmentData = useCallback((item: any): Shipment => {
    try {
      const packageData = item.package || item.combinedPackage || {};
      const packageDescription = packageData?.description || '';
      const packageWeight = packageData?.weight ? parseFloat(packageData.weight) : 0;
      const packageQuantity = packageData?.quantity || 1;
      const status = mapStateToStatus(item.state || '');
      return {
        shipment_id: item.id || 0,
        id: item.id?.toString() || `ID-${Math.random().toString(36).slice(2, 9)}`,
        name: packageDescription || `Shipment #${item.id || 'Unknown'}`,
        status: status,
        createdDate: item.created_at || new Date().toISOString(),
        shippedDate: item.shipped_date || undefined,
        deliveredDate: item.delivered_date || undefined,
        trackingNumber: item.tracking_number || undefined,
        carrier: item.carrier || undefined,
        estimatedDelivery: item.estimated_delivery || undefined,
        totalWeight: packageWeight,
        totalValue: 0,
        packageCount: packageQuantity,
        destination: {
          name: item.reciver_Address?.name || '',
          street: item.reciver_Address?.address || '',
          city: item.reciver_Address?.city || '',
          state: item.reciver_Address?.state || '',
          postalCode: item.reciver_Address?.zip || '',
          country: item.reciver_Address?.country || '',
          phone: '',
        },
        packages: [],
        events: [],
        cost: {
          shipping: 0,
          insurance: 0,
          consolidation: 0,
          tax: 0,
          other: 0,
          total: parseFloat(item.cost || '0'),
          paymentStatus: item.PaymentTransaction?.state || 'INITIATED',
          url: item.PaymentTransaction?.data?.url || '',
        },
      };
    } catch (error) {
      return {
        shipment_id: 0,
        id: `error-${Math.random().toString(36).slice(2, 9)}`,
        name: 'Error processing shipment',
        status: 'processing',
        createdDate: new Date().toISOString(),
        shippedDate: undefined,
        deliveredDate: undefined,
        trackingNumber: undefined,
        carrier: undefined,
        estimatedDelivery: undefined,
        totalWeight: 0,
        totalValue: 0,
        packageCount: 0,
        destination: {
          name: '',
          street: '',
          city: '',
          state: '',
          postalCode: '',
          country: '',
          phone: '',
        },
        packages: [],
        events: [],
        cost: {
          shipping: 0,
          insurance: 0,
          consolidation: 0,
          tax: 0,
          other: 0,
          total: 0,
          paymentStatus: 'INITIATED',
          url: '',
        },
      };
    }
  }, [mapStateToStatus]);

  // Fetch shipments data
  const fetchShipments = useCallback(async () => {
    dispatch({ type: 'FETCH_SHIPMENTS_FAILURE', payload: null });
    try {
      const response = await ShipmentService.getShipment();
      if (response && Array.isArray(response)) {
        const validItems = response.filter(item => item != null);
        const transformedShipments: Shipment[] = validItems.map(transformShipmentData);
        dispatch({ type: 'FETCH_SHIPMENTS_SUCCESS', payload: transformedShipments });
      } else {
        throw new Error('Invalid response format');
      }
    } catch (error) {
      dispatch({ type: 'FETCH_SHIPMENTS_FAILURE', payload: 'Failed to load shipments. Please try again later.' });
    }
  }, [transformShipmentData, dispatch]);

  // Load data on component mount
  useEffect(() => {
    fetchShipments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Update filtering state based on form values
  // Remove local isFiltering effect, now derived from context

  // Filter and sort shipments
  const processedShipments = useMemo(() => {
    const filtered = shipments.filter(shipment => {
      const matchesStatus = filters.statusFilter === 'all' || shipment.status === filters.statusFilter;
      return matchesStatus;
    });
    return [...filtered].sort((a, b) => {
      if (a.status === 'shipped' && b.status !== 'shipped') return 1;
      if (b.status === 'shipped' && a.status !== 'shipped') return -1;
      return new Date(b.createdDate).getTime() - new Date(a.createdDate).getTime();
    });
  }, [shipments, filters.statusFilter]);

  // Handle refresh data
  const handleRefresh = useCallback(() => {
    fetchShipments();
  }, [fetchShipments]);

  return (
    <Container size="xl" py="md">
      <Stack>
        <PageTitle
          title="My Shipments"
          description="Track and manage your package shipments"
          icon={<IconTruck size={24} />}
          breadcrumbs={[
            { title: 'Dashboard', href: '/dashboard' },
            { title: 'Shipments' }
          ]
          }
        />

        <SegmentedControl
          value={filters.statusFilter}
          onChange={(value) => {
            reset({ statusFilter: value as FilterFormValues['statusFilter'] });
            dispatch({ type: 'SET_FILTER', payload: { ...filters, statusFilter: value as FilterFormValues['statusFilter'] } });
          }}
          data={[
            { label: 'All', value: 'all' },
            {
              label: (
                <Flex align="center" gap={4}>
                  <IconPackageImport style={iconStyle} />
                  <span>Processing</span>
                </Flex>
              ),
              value: 'processing'
            },
            {
              label: (
                <Flex align="center" gap={4}>
                  <IconTruck style={iconStyle} />
                  <span>Shipped</span>
                </Flex>
              ),
              value: 'shipped'
            },
            {
              label: (
                <Flex align="center" gap={4}>
                  <IconTruckDelivery style={iconStyle} />
                  <span>Delivered</span>
                </Flex>
              ),
              value: 'delivered'
            }
          ]}
        />

        {/* Results Counter */}
        <Flex justify="space-between" align="center" mb="xs">
          <Flex align="center" gap="xs">
            <Text fw={500} component="span">
              Shipment Results
            </Text>
            <Badge size="sm" variant="light">
              {processedShipments.length}
            </Badge>
          </Flex>
        </Flex>

        {/* Error Message */}
        {error && (
          <Alert color="red" title="Error" icon={<IconAlertCircle />} mb="md">
            {error}
            <Button variant="subtle" color="red" onClick={handleRefresh} mt="xs">
              Try Again
            </Button>
          </Alert>
        )}

        {/* Shipments List Section */}
        <div style={{ position: 'relative', minHeight: loading ? '200px' : 'auto', overflow: 'hidden' }}>
          <LoadingOverlay visible={loading} zIndex={1000} overlayProps={{ radius: "sm", blur: 2 }} />

          {!loading && !error && processedShipments.length === 0 ? (
            <EmptyState
              isFiltering={isFiltering}
              onClearFilters={() => {
                reset({ statusFilter: 'all' });
                dispatch({ type: 'SET_FILTER', payload: { ...filters, statusFilter: 'all' } });
              }}
            />
          ) : (
            !loading && !error && (
              <ShipmentList
                shipments={processedShipments}
              />
            )
          )}
        </div>
      </Stack>
    </Container>
  );
};

// Wrap with ShipmentProvider for context
const ShipmentsPage = () => (
  <ShipmentProvider>
    <Shipments />
  </ShipmentProvider>
);
export default ShipmentsPage;
